import { FC } from "react";
import { useTranslation } from "react-i18next";

import { VenueBoardPublic } from "../../../services/venueService";
import { isMe, resolveBoardRows } from "../leaderboardModel";
import { BoardGap, BoardRow, BoardRowPlaceholders } from "./BoardRows";

const KEY = "leaderboards.VenuePage.";

export interface LegendBoardProps {
    board: VenueBoardPublic;
    /** The player's own appearances in the window, for the pinned row when off the board. */
    yourWindowApps: number;
    myDisplayName?: string | null;
    onBoards: boolean;
}

/**
 * The Local Legend board (SCORZA-739 design handoff, reference/02).
 *
 * The board is the same five-row table as the metric boards, gold avatar on rank 1. When the
 * player is outside those five their own row is pinned to the bottom after a `•••` break, so the
 * section always answers "where am I?" — which is the only question that makes a leaderboard
 * worth opening twice. That trimming rule lives in `resolveBoardRows`, shared with the metric
 * boards.
 */
export const LegendBoard: FC<LegendBoardProps> = ({
    board,
    yourWindowApps,
    myDisplayName,
    onBoards,
}) => {
    const { t } = useTranslation();
    const standings = board.standings || [];
    const leader = board.localLegend || standings[0] || null;

    const resolved = resolveBoardRows(
        standings,
        board.playerCount || standings.length,
        myDisplayName,
        onBoards
    );
    const mine = standings.find((standing) =>
        isMe(standing.displayName, myDisplayName, onBoards)
    );

    const behindLeader = leader
        ? Math.max(leader.matchCount - (mine?.matchCount ?? yourWindowApps), 0)
        : 0;

    return (
        <>
            <div className="venue-legend-list" data-testid="venue-standings">
                {resolved.rows.map((standing, index) => {
                    const you = isMe(
                        standing.displayName,
                        myDisplayName,
                        onBoards
                    );
                    return (
                        <div key={`${standing.rank}-${standing.displayName}`}>
                            {index === resolved.gapBefore && <BoardGap />}
                            <BoardRow
                                rank={standing.rank}
                                displayName={standing.displayName}
                                value={standing.matchCount}
                                unit={t(KEY + "matchesUnit")}
                                you={you}
                                gold={standing.rank === 1}
                                testId="venue-standing"
                            />
                        </div>
                    );
                })}

                <BoardRowPlaceholders filled={resolved.rows.length} />

                {resolved.youState !== "onBoard" &&
                    resolved.youState !== "hidden" && (
                        <>
                            <BoardGap />
                            <BoardRow
                                rank={
                                    resolved.outsideCutLabel ?? (
                                        <span aria-hidden="true">—</span>
                                    )
                                }
                                displayName={t(KEY + "notOnBoardYet")}
                                initialsFrom={myDisplayName}
                                value={yourWindowApps}
                                unit={t(KEY + "matchesUnit")}
                                you
                                testId="venue-standing-you"
                            />
                        </>
                    )}
            </div>

            {leader && behindLeader > 0 && (
                <div
                    className="venue-legend-nudge"
                    data-testid="venue-legend-nudge"
                >
                    🔥{" "}
                    {t(KEY + "legendNudge", {
                        count: behindLeader,
                        name: leader.displayName,
                    })}
                </div>
            )}
        </>
    );
};
